/**
 * TPRA Home Summary API Request Hook
 * Fetches summary statistics for the dashboard cards
 */

import { get } from "@/infra/rest";
import { APIEndpoints } from "@/shared/constants/Endpoints";
import { tpraHomeQueryKeys } from "@/shared/constants/QueryKeys";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { HomeSummaryResponse } from "../types";
import { HomeSummaryResponseSchema } from "../validation/schemas";

/**
 * Fetches home summary data from the API
 * @returns Validated summary response
 * @throws Error if API request fails or response validation fails
 */
async function fetchHomeSummary(): Promise<HomeSummaryResponse> {
  const response = await get<HomeSummaryResponse>({
    url: APIEndpoints.TPRAHomeSummary,
  });

  // Validate response with Zod schema
  const validated = HomeSummaryResponseSchema.parse(response);

  return validated;
}

/**
 * React Query hook for fetching TPRA home summary
 *
 * Provides:
 * - Automatic caching with TanStack Query
 * - Loading and error states
 * - Automatic refetching on mount
 * - Retry logic for failed requests
 *
 * @returns React Query result with summary data
 *
 * @example
 * ```typescript
 * function SummaryCards() {
 *   const { data, isLoading, isError, refetch } = useGetHomeSummary();
 *
 *   if (isLoading) return <SummaryCardsSkeleton />;
 *   if (isError) return <SectionError onRetry={refetch} />;
 *
 *   return <SummaryCardsContent data={data} />;
 * }
 * ```
 */
export function useGetHomeSummary(): UseQueryResult<HomeSummaryResponse, Error> {
  return useQuery({
    queryKey: tpraHomeQueryKeys.summary(),
    queryFn: fetchHomeSummary,

    // Caching Strategy
    staleTime: 0, // Always refetch for fresh data
    gcTime: 10 * 60 * 1000, // 10 minutes cache retention

    // Retry Strategy (exponential backoff)
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),

    // Refetch Behavior
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });
}
